import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderSlot as _renderSlot, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-f6652faa"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "frisbuy-customer-review__top-section" }
const _hoisted_2 = {
  key: 0,
  class: "frisbuy-customer-review__text"
}
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "frisbuy-customer-review__text--prefix" }, " Преимущества: ", -1))
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "frisbuy-customer-review__text--prefix" }, " Недостатки: ", -1))
const _hoisted_5 = {
  key: 2,
  class: "frisbuy-customer-review__media-section"
}

import MediaList from "./MediaList.vue";
import ReviewAuthor from "./ReviewAuthor.vue";
import { imageWithPostId, postMedia } from "../interfaces";
import {
  computed,
  nextTick,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
} from "vue";
import { addSchemaToPage } from "@/common/services/SchemaOrg/SchemaOrg";

//#region Schema.org
type sectionConfig = {
  order: number;
  margin: string;
};

type ProductLinkInfo = {
  link: string;
  title: string;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'CustomerReview',
  props: {
    customerName: {},
    reviewDate: {},
    rating: {},
    mediaList: {},
    selectedMediaThumbnailUrl: {},
    isShowMedia: { type: Boolean, default: true },
    negativeComment: {},
    positiveComment: {},
    text: {},
    blocksOrder: {},
    sectionsMarginTop: {},
    topSectionConfig: { default: () => {
      return {
        order: 0,
        margin: "0",
      };
    } },
    isSoftStarIcon: { type: Boolean },
    avatarColorIndex: {},
    authorView: { default: "default" }
  },
  emits: ["selectMedia", "popupReadMoreToggle", "selectReview"],
  setup(__props: any, { emit: __emit }) {

onBeforeMount(() => {
  props.mediaList?.forEach((media) => {
    media.linkList.forEach((link) => {
      const Product = link;
      const bestRating = 5;
      // NOTE: if ratingValue === 0 and worstRating === 1, then it will be error
      const worstRating = 1;
      if (!Product) {
        return;
      }

      /* NOTE:
        Google always need to get `ratingValue` and `reviewCount`.
        But sometimes this information may not be available
      */
      if (
        !Product.sku ||
        !Product.sku?.avgRating ||
        !Product.sku?.reviewsCount
      ) {
        return;
      }

      const reviewRating =
        props.rating && props.rating > 0 ? props.rating : bestRating;

      const SchemaorgProduct = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: Product.title,
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: Product.sku.avgRating,
          bestRating: bestRating,
          worstRating: worstRating,
          reviewCount: Product.sku.reviewsCount,
        },

        review: [
          {
            "@type": "Review",

            author: {
              "@type": "Person",
              name: props.customerName,
            },
            datePublished: props.reviewDate,
            reviewBody: `${props.text}\n${props.positiveComment}\n${props.negativeComment}`,

            reviewRating: {
              "@type": "Rating",
              bestRating: bestRating,
              ratingValue: reviewRating,
              worstRating: worstRating,
            },
          },
        ],
      };

      addSchemaToPage(SchemaorgProduct);
    });
  });
});
//#endregion Schema.org

const props = __props;

const emit = __emit;

function selectPostWithPhoto(media: imageWithPostId) {
  emit("selectMedia", media);
}

function onReviewClick() {
  if (isPopupReview.value) {
    return;
  }

  emit("selectReview");
}

const isPopupReview = computed(() => props.authorView === "popup");
const isReadMoreExpanded = ref(false);
const isOverflowContent = ref(false);
const positiveTextRef = ref<HTMLElement | null>(null);
const negativeTextRef = ref<HTMLElement | null>(null);
const commentTextRef = ref<HTMLElement | null>(null);
const contentMainRef = ref<HTMLElement | null>(null);

function detectOverflowContent() {
  if (!isPopupReview.value) {
    const textBlocks = [
      positiveTextRef.value,
      negativeTextRef.value,
      commentTextRef.value,
    ].filter((el): el is HTMLElement => Boolean(el));

    isOverflowContent.value = textBlocks.some((el) => {
      return el.scrollHeight - el.clientHeight > 1;
    });
    return;
  }

  const contentEl = contentMainRef.value;
  if (!contentEl) {
    isOverflowContent.value = false;
    return;
  }

  isOverflowContent.value = contentEl.scrollHeight > 432;
}

onMounted(() => {
  nextTick(detectOverflowContent);
  window.addEventListener("resize", detectOverflowContent);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", detectOverflowContent);
});

watch(
  () => [
    props.customerName,
    props.reviewDate,
    props.positiveComment,
    props.negativeComment,
    props.text,
    props.authorView,
  ],
  () => {
    isReadMoreExpanded.value = false;
    if (isPopupReview.value) {
      emit("popupReadMoreToggle", false);
    }
    nextTick(detectOverflowContent);
  },
  { deep: true }
);

//#region unique product links
const uniqueProductLinks = ref<Array<ProductLinkInfo>>([]);
onBeforeMount(() => {
  uniqueProductLinks.value = getUniqueProductLinks();
});

watch(
  () => props.mediaList,
  () => {
    uniqueProductLinks.value = getUniqueProductLinks();
  }
);

function getUniqueProductLinks(): Array<ProductLinkInfo> {
  const uniqueLinks: Array<ProductLinkInfo> = [];
  const uniqueLinksInStringifyFormat = new Set<string>();

  props.mediaList?.forEach((media) => {
    media.linkList.forEach((link) => {
      const productInfo = {
        link: link.link,
        title: link.title,
      };

      uniqueLinksInStringifyFormat.add(JSON.stringify(productInfo));
    });
  });

  uniqueLinksInStringifyFormat.forEach((linkInStringifyFormat) => {
    uniqueLinks.push(JSON.parse(linkInStringifyFormat));
  });

  return uniqueLinks;
}
//#endregion unique product links

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["frisbuy-customer-review", {
      'frisbuy-customer-review--modal-popup': isPopupReview.value,
      'frisbuy-customer-review--modal-popup-expanded':
        isPopupReview.value && isReadMoreExpanded.value,
    }]),
    onClick: onReviewClick
  }, [
    _createElementVNode("div", {
      class: _normalizeClass({
        'frisbuy-customer-review__content-main': !isPopupReview.value,
        'frisbuy-customer-review__content-main-modal-popup': isPopupReview.value,
        'frisbuy-customer-review__content-main-modal-popup--collapsed':
          isPopupReview.value && isOverflowContent.value && !isReadMoreExpanded.value,
        'frisbuy-customer-review__content-main-modal-popup--expanded':
          isPopupReview.value && isReadMoreExpanded.value,
      }),
      ref_key: "contentMainRef",
      ref: contentMainRef
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(ReviewAuthor, {
          customerName: props.customerName,
          reviewDate: props.reviewDate,
          rating: props.rating,
          isSoftStarIcon: props.isSoftStarIcon,
          avatarColorIndex: props.avatarColorIndex,
          view: props.authorView
        }, null, 8, ["customerName", "reviewDate", "rating", "isSoftStarIcon", "avatarColorIndex", "view"]),
        _renderSlot(_ctx.$slots, "topRight")
      ]),
      (props.positiveComment || props.negativeComment || props.text)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (props.positiveComment)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["frisbuy-customer-review__text--positive", {
            'frisbuy-customer-review__text-block--clamped':
              !isPopupReview.value && !isReadMoreExpanded.value,
          }]),
                  ref_key: "positiveTextRef",
                  ref: positiveTextRef
                }, [
                  _hoisted_3,
                  _createElementVNode("span", null, _toDisplayString(props.positiveComment), 1)
                ], 2))
              : _createCommentVNode("", true),
            (props.negativeComment)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: _normalizeClass(["frisbuy-customer-review__text--negative", {
            'frisbuy-customer-review__text-block--clamped':
              !isPopupReview.value && !isReadMoreExpanded.value,
          }]),
                  ref_key: "negativeTextRef",
                  ref: negativeTextRef
                }, [
                  _hoisted_4,
                  _createElementVNode("span", null, _toDisplayString(props.negativeComment), 1)
                ], 2))
              : _createCommentVNode("", true),
            (props.text)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 2,
                  class: _normalizeClass(["frisbuy-customer-review__text--comment", {
            'frisbuy-customer-review__text-block--clamped':
              !isPopupReview.value && !isReadMoreExpanded.value,
          }]),
                  ref_key: "commentTextRef",
                  ref: commentTextRef
                }, [
                  _createElementVNode("span", null, _toDisplayString(props.text), 1)
                ], 2))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ], 2),
    (isOverflowContent.value && !isReadMoreExpanded.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          class: "frisbuy-customer-review__read-more",
          type: "button",
          onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => {
        isReadMoreExpanded.value = true;
        if (isPopupReview.value) emit('popupReadMoreToggle', true);
      }, ["stop"]))
        }, " Читать далее "))
      : _createCommentVNode("", true),
    (isPopupReview.value && isOverflowContent.value && isReadMoreExpanded.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 1,
          class: "frisbuy-customer-review__read-more frisbuy-customer-review__read-more--hide",
          type: "button",
          onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => {
        isReadMoreExpanded.value = false;
        emit('popupReadMoreToggle', false);
      }, ["stop"]))
        }, " Скрыть "))
      : _createCommentVNode("", true),
    (props.isShowMedia && props.mediaList && props.mediaList.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(MediaList, {
            mediaList: props.mediaList,
            mediaAspectRatio: "1 / 1",
            gap: "8px",
            wrap: "wrap",
            mediaWidth: "48px",
            maxMediaItems: 3,
            isShowCountBlock: "",
            onSelectMedia: _cache[2] || (_cache[2] = (media) => selectPostWithPhoto(media))
          }, null, 8, ["mediaList"])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})